// ============================================
// ConverseTech Frontend - API Client
// ============================================

import type {
  DashboardOverview,
  Session,
  GetUserResponse,
  UpdateUserRequest,
  StartSessionRequest,
  StartSessionResponse,
  TrainingMode,
  Mistake,
  MistakeSummary,
  DailyRitual,
  RitualRecommendation,
} from '@conversetech/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

/** Custom error class for API errors with status code and user-friendly messages */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly userMessage: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Map HTTP status codes to user-friendly error messages */
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Please sign in to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again later.',
  502: 'Service temporarily unavailable. Please try again.',
  503: 'Service is currently unavailable. Please try again later.',
};

/**
 * Get a user-friendly error message for an HTTP status code.
 */
function getUserMessage(statusCode: number, serverMessage?: string): string {
  return serverMessage || ERROR_MESSAGES[statusCode] || 'An unexpected error occurred.';
}

/**
 * Get auth token from Clerk session.
 */
async function getAuthToken(): Promise<string | null> {
  // @ts-expect-error - Clerk types not available at compile time
  const clerk = window.Clerk;
  if (!clerk) return null;
  return clerk.session?.getToken() || null;
}

/**
 * Authenticated fetch wrapper with error handling.
 *
 * Automatically adds authorization headers and handles common error cases.
 * Throws ApiError with user-friendly messages on failure.
 *
 * @param endpoint - API endpoint (e.g., '/api/users/me')
 * @param options - Standard fetch options
 * @returns Parsed JSON response
 * @throws ApiError on non-2xx responses
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: undefined }));
    const userMessage = getUserMessage(response.status, errorBody.error);
    throw new ApiError(
      errorBody.error || `Request failed with status ${response.status}`,
      response.status,
      userMessage
    );
  }

  return response.json();
}

// === User API ===

export async function getCurrentUser(): Promise<GetUserResponse> {
  return apiFetch<GetUserResponse>('/api/users/me');
}

export async function updateUser(data: UpdateUserRequest): Promise<GetUserResponse> {
  return apiFetch<GetUserResponse>('/api/users/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// === Dashboard API ===

export async function getDashboardOverview(): Promise<DashboardOverview> {
  return apiFetch<DashboardOverview>('/api/dashboard/overview');
}

// === Sessions API ===

export async function startSession(mode: TrainingMode): Promise<StartSessionResponse> {
  return apiFetch<StartSessionResponse>('/api/sessions/start', {
    method: 'POST',
    body: JSON.stringify({ mode } as StartSessionRequest),
  });
}

export async function endSession(sessionId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/api/sessions/${sessionId}/end`, {
    method: 'POST',
  });
}

export async function getSessions(): Promise<{ sessions: Session[] }> {
  return apiFetch<{ sessions: Session[] }>('/api/sessions');
}

export async function getSession(sessionId: string): Promise<{ session: Session }> {
  return apiFetch<{ session: Session }>(`/api/sessions/${sessionId}`);
}

export async function getSessionMistakes(sessionId: string): Promise<{ mistakes: Mistake[] }> {
  return apiFetch<{ mistakes: Mistake[] }>(`/api/sessions/${sessionId}/mistakes`);
}

// === Mistakes API ===

export async function getMistakeSummary(): Promise<MistakeSummary> {
  return apiFetch<MistakeSummary>('/api/dashboard/mistakes');
}

// === Ritual API ===

export interface RitualResponse {
  ritual: DailyRitual;
  recommendation?: RitualRecommendation;
  isNew: boolean;
}

export async function getTodayRitual(): Promise<RitualResponse> {
  return apiFetch<RitualResponse>('/api/dashboard/ritual');
}

export async function completeRitual(sessionId: string): Promise<{ ritual: DailyRitual; success: boolean }> {
  return apiFetch<{ ritual: DailyRitual; success: boolean }>('/api/dashboard/ritual/complete', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

// === Health API ===

export async function getHealth(): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE}/api/health`);
  return response.json();
}
